import React, { useEffect, useRef, useState } from 'react';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import { messageTime } from '../../helpers/chatTime';
import { amendWindowLeft, canAmend, chatImageUrl } from '../../helpers/chatMedia';
import './Chat.scss';

/* Roughly how tall the menu stands. Only used to decide which way it opens,
   so it is a threshold rather than a measurement — the menu is not in the
   document yet at the moment the choice has to be made. */
const MENU_HEIGHT = 190;

/* The tick beside your own message: waiting, sent, delivered, read. */
const StatusTick = ({ status }) => {
    if (status === 'pending') return <ScheduleRoundedIcon className="chat-tick" />;
    if (status === 'failed') return <ErrorOutlineRoundedIcon className="chat-tick is-failed" />;
    if (status === 'read') return <DoneAllRoundedIcon className="chat-tick is-read" />;
    if (status === 'delivered') return <DoneAllRoundedIcon className="chat-tick" />;
    return <DoneRoundedIcon className="chat-tick" />;
};

/* The strip a reply carries at the top of its bubble.

   The words shown are the copy taken when the reply was sent, not a live
   read of the original — so a quote never rewrites itself when the message
   it answers is edited, and never empties itself when that message is
   deleted. Tapping it jumps to the original if it is still in the thread. */
const Quote = ({ quote, myCode, onJump }) => {
    if (!quote) return null;
    const author = String(quote.senderCode) === String(myCode)
        ? 'You'
        : (quote.senderName || 'Them');
    return (
        <button
            type="button"
            className="chat-quote"
            onClick={() => onJump && onJump(quote.messageId)}
            title="Go to the original message"
        >
            <span className="chat-quote-who">{author || 'Message'}</span>
            <span className="chat-quote-text">
                {quote.kind === 'image' && <ImageRoundedIcon />}
                {quote.text || 'Photo'}
            </span>
        </button>
    );
};

/* One message.

   The actions live here rather than in the thread so a menu opening only
   re-renders the bubble it belongs to — with a long history on screen,
   hanging that state off the list would repaint every bubble on each hover. */
const MessageBubble = ({
    message, mine, grouped, myCode, highlighted,
    onReply, onEdit, onDelete, onRetry, onOpenImage, onJumpToQuote,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [imageReady, setImageReady] = useState(false);
    /* Which way the menu opens. It prefers upwards, which is where the eye
       already is, and only drops below when the message is near the top of
       the thread and there is nothing above to open into. */
    const [dropsDown, setDropsDown] = useState(false);
    const wrapRef = useRef(null);
    const triggerRef = useRef(null);

    /* A menu left open behind a click elsewhere would sit there until the
       bubble is hovered again, so it closes on any click outside it and on
       Escape — the two ways anyone expects to dismiss a popover. */
    useEffect(() => {
        if (!menuOpen) return undefined;
        const away = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setMenuOpen(false);
                setConfirming(false);
            }
        };
        const escape = (event) => {
            if (event.key === 'Escape') { setMenuOpen(false); setConfirming(false); }
        };
        document.addEventListener('mousedown', away);
        document.addEventListener('keydown', escape);
        return () => {
            document.removeEventListener('mousedown', away);
            document.removeEventListener('keydown', escape);
        };
    }, [menuOpen]);

    const close = () => { setMenuOpen(false); setConfirming(false); };

    /* Measured against the scrolling thread rather than the window: inside a
       popup it is the thread that clips, and it is far shorter than the
       screen. Decided before the menu is painted so it never opens one way
       and then jumps the other. */
    const openMenu = () => {
        if (menuOpen) { close(); return; }
        const trigger = triggerRef.current;
        const scroller = trigger && trigger.closest('.chat-thread-scroll');
        if (trigger && scroller) {
            const room = trigger.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
            setDropsDown(room < MENU_HEIGHT);
        }
        setMenuOpen(true);
    };

    const amendable = canAmend(message, myCode);
    const attachment = message.attachment;

    /* A picture the sender has just chosen exists in two places: as the blob
       the browser already holds, and — once the upload finishes — as a file
       on the server.

       The stored copy is what the <img> points at, because that is the one
       that survives a reload. The blob is painted behind it as a background
       until those bytes have arrived, so the handover is invisible rather
       than a bubble that blanks and fills again. Before the upload is done
       there is no stored copy yet, so the blob is all there is. */
    const stored = attachment && attachment.file ? chatImageUrl(attachment.file) : '';
    const imageSrc = stored || message.previewUrl || '';
    const underlay = !imageReady && stored && message.previewUrl ? message.previewUrl : '';

    /* Reserve the shape before the bytes arrive, so a thread of pictures does
       not shuffle itself as each one lands. */
    const ratio = attachment && attachment.width && attachment.height
        ? attachment.width / attachment.height
        : 0;

    if (message.deleted) {
        return (
            <div
                className={`chat-row${mine ? ' is-mine' : ''}${grouped ? ' is-grouped' : ''}`}
                data-mid={String(message._id)}
            >
                <div className="chat-bubble is-deleted">
                    <BlockRoundedIcon />
                    <span className="chat-bubble-text">This message was deleted</span>
                    <span className="chat-bubble-meta">{messageTime(message.createdAt)}</span>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`chat-row${mine ? ' is-mine' : ''}${grouped ? ' is-grouped' : ''}`
                + `${highlighted ? ' is-highlighted' : ''}${menuOpen ? ' has-menu' : ''}`}
            data-mid={String(message._id)}
            ref={wrapRef}
        >
            <div className="chat-row-line">
                <div
                    className={`chat-bubble${message.status === 'failed' ? ' is-failed' : ''}`
                        + `${attachment ? ' has-image' : ''}`}
                >
                    <Quote quote={message.replyTo} myCode={myCode} onJump={onJumpToQuote} />

                    {attachment && (
                        <button
                            type="button"
                            className={`chat-image${imageReady ? ' is-ready' : ''}`}
                            style={{
                                ...(ratio ? { aspectRatio: String(ratio) } : {}),
                                ...(underlay ? { backgroundImage: `url(${underlay})` } : {}),
                            }}
                            onClick={() => onOpenImage(message)}
                            aria-label="Open image"
                        >
                            <img
                                src={imageSrc}
                                alt={attachment.name || 'Shared image'}
                                loading="lazy"
                                onLoad={() => setImageReady(true)}
                            />
                            {message.status === 'pending' && (
                                <span className="chat-image-progress"><i /></span>
                            )}
                        </button>
                    )}

                    {message.text && <span className="chat-bubble-text">{message.text}</span>}

                    <span className="chat-bubble-meta">
                        {message.editedAt && <em className="chat-edited">edited</em>}
                        {messageTime(message.createdAt)}
                        {mine && <StatusTick status={message.status} />}
                    </span>
                </div>

                {/* Sits outside the bubble so it never overlaps the words, and
                    is only reachable on a message that has actually been
                    stored — there is nothing to reply to or amend until then. */}
                {!String(message._id).startsWith('pending-') && (
                    <div className="chat-actions">
                        <button
                            type="button"
                            className="chat-action"
                            title="Reply"
                            onClick={() => { close(); onReply(message); }}
                        >
                            <ReplyRoundedIcon />
                        </button>
                        <button
                            type="button"
                            className="chat-action"
                            title="More"
                            ref={triggerRef}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            onClick={openMenu}
                        >
                            <MoreVertRoundedIcon />
                        </button>

                        {menuOpen && (
                            <div
                                className={`chat-menu${mine ? ' is-mine' : ''}${dropsDown ? ' drops-down' : ''}`}
                                role="menu"
                            >
                                {!confirming && (
                                    <>
                                        <button type="button" onClick={() => { close(); onReply(message); }}>
                                            <ReplyRoundedIcon /> Reply
                                        </button>
                                        {message.text && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    navigator.clipboard?.writeText(message.text);
                                                    close();
                                                }}
                                            >
                                                <ContentCopyRoundedIcon /> Copy text
                                            </button>
                                        )}
                                        {mine && (
                                            <>
                                                <span className="chat-menu-rule" />
                                                <button
                                                    type="button"
                                                    disabled={!amendable}
                                                    title={amendable
                                                        ? amendWindowLeft(message)
                                                        : 'Messages can only be edited within 24 hours'}
                                                    onClick={() => { close(); onEdit(message); }}
                                                >
                                                    <EditRoundedIcon /> Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className="is-danger"
                                                    disabled={!amendable}
                                                    title={amendable
                                                        ? amendWindowLeft(message)
                                                        : 'Messages can only be deleted within 24 hours'}
                                                    onClick={() => setConfirming(true)}
                                                >
                                                    <DeleteOutlineRoundedIcon /> Delete
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}

                                {/* Asked for inside the menu rather than in a
                                    dialog over the page: a delete is worth a
                                    second press, not a modal that hides the
                                    message being deleted. */}
                                {confirming && (
                                    <div className="chat-menu-confirm">
                                        <p>Delete this message for everyone?</p>
                                        <div>
                                            <button type="button" onClick={() => setConfirming(false)}>Cancel</button>
                                            <button
                                                type="button"
                                                className="is-danger"
                                                onClick={() => { close(); onDelete(message); }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {mine && message.status === 'failed' && (
                <button type="button" className="chat-retry" onClick={() => onRetry(message)}>
                    Not sent — tap to retry
                </button>
            )}
        </div>
    );
};

export default React.memo(MessageBubble);
